// CachingDetail.jsx
// 5) Caching — detailed guide
// Left TOC + scroll-spy + MAIN "Template + Example (Facebook-like Home Feed)" per section
// Right Sponsored (AdSense). Bootstrap UI. React Router v4/v5 compatible.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ====================================================================== */
/*  AdSense                                                               */
/* ====================================================================== */

const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
      return;
    }

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  Small UI helpers (readability-first)                                   */
/* ====================================================================== */

function Section({ id, title, subtitle, children }) {
  return (
    <section id={id} className="mb-5">
      <div className="mb-2">
        <h2 className="h4 mb-1">{title}</h2>
        {subtitle ? <div className="text-muted">{subtitle}</div> : null}
      </div>

      {children}

      <div className="mt-3">
        <a href="#top" className="small text-muted">
          ↑ Back to top
        </a>
      </div>
    </section>
  );
}

function Badge({ tone = "secondary", children, className = "" }) {
  return (
    <span className={`badge bg-${tone} ${className}`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function Muted({ children }) {
  return (
    <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
      {children}
    </div>
  );
}

function Card({ title, badge, children, className = "" }) {
  return (
    <div className={`card border-0 shadow-sm ${className}`}>
      <div className="card-body">
        {(title || badge) && (
          <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
            <div className="fw-semibold">{title}</div>
            {badge ? badge : null}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function Template({ title = "Template", children }) {
  return (
    <Card className="mt-3" title={title} badge={<Badge tone="secondary">Template</Badge>}>
      <Muted>{children}</Muted>
    </Card>
  );
}

function Example({ title = "Example (Facebook-like Home Feed)", children }) {
  return (
    <Card className="mt-3" title={title} badge={<Badge tone="success">Example</Badge>}>
      <Muted>{children}</Muted>
    </Card>
  );
}

function Takeaway({ children, tone = "primary" }) {
  return (
    <div className={`alert alert-${tone} py-2 mt-3 mb-0`} role="note">
      <div className="fw-semibold small mb-1">Interview line</div>
      <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
        {children}
      </div>
    </div>
  );
}

function Pitfalls({ items }) {
  return (
    <div className="alert alert-warning small mt-3 mb-0">
      <div className="fw-semibold mb-1">Common pitfalls</div>
      <ul className="mb-0">
        {items.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

function Code({ title, children }) {
  return (
    <Card className="mt-3" title={title} badge={<Badge tone="secondary">Snippet</Badge>}>
      <pre
        className="bg-light rounded p-3 border small mb-0"
        style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
      >
        <code>{children}</code>
      </pre>
    </Card>
  );
}

/* ====================================================================== */
/*  Example context (MAIN content)                                         */
/* ====================================================================== */

function ExampleContextCard({ system }) {
  return (
    <Card
      className="mb-4"
      title="Example context"
      badge={<Badge tone="primary">{system.name}</Badge>}
    >
      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div className="fw-semibold small mb-1">Read path</div>
          <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
            {system.readPath}
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="fw-semibold small mb-1">Write path</div>
          <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
            {system.writePath}
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="fw-semibold small mb-1">Engagement</div>
          <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
            {system.engagementPath}
          </div>
        </div>
      </div>

      <hr className="my-3" />

      <div className="fw-semibold small mb-1">Caching goals for this system</div>
      <ul className="mb-0 text-muted small">
        {system.goals.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </Card>
  );
}

/* ====================================================================== */
/*  TOC                                                                    */
/* ====================================================================== */

const TOC = [
  { id: "where", label: "A) Where to cache" },
  { id: "what", label: "B) What to cache" },
  { id: "invalidation", label: "C) Invalidation" },
  { id: "consistency", label: "D) Consistency patterns" },
  { id: "eviction", label: "E) Eviction & sizing" },
  { id: "stampede", label: "F) Stampede protection" },
  { id: "metrics", label: "G) Metrics & alarms" },
  { id: "examples", label: "H) Practical examples" },
  { id: "keys-headers", label: "I) Keys & HTTP headers" },
  { id: "oneliners", label: "J) One-liners" },
];

/* ====================================================================== */
/*  Page                                                                   */
/* ====================================================================== */

export default function CachingDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  const homeFeed = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      readPath:
        "GET /feed → fetch candidate postIds → hydrate posts/media → rank → rules → cursor",
      writePath:
        "POST /post → validate → store → async fanout/index/counters/notifications",
      engagementPath:
        "POST /like | /comment → idempotent write → counters → ranking features update",
      goals: [
        "Reduce p95/p99 latency for GET /feed (hydration is expensive)",
        "Protect DB and downstream services from read amplification",
        "Avoid cache stampedes for hot users and hot posts",
        "Keep privacy boundaries (don’t leak personalized feed to wrong user)",
        "Lower origin egress for media via CDN hit ratio",
      ],
    }),
    []
  );

  /* -------------------- scroll-spy -------------------- */
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Caching</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 rounded-3 p-3 p-md-4 text-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(25,135,84,.08), rgba(108,117,125,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-2">5) Caching — detailed guide</h1>
          <p className="text-muted mb-0">
            Where to cache, what to cache, how to invalidate, and how to avoid stampedes—so you hit SLOs
            without privacy leaks or “green cache, red prod.”
          </p>
        </div>
      </header>

      {/* layout: LEFT TOC + MAIN + RIGHT Sponsored */}
      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <Card title="On this page">
              <nav className="nav flex-column small">
                {TOC.map(({ id, label }) => (
                  <a
                    key={id}
                    href={`#${id}`}
                    className={`nav-link px-0 ${
                      activeId === id ? "fw-semibold text-success" : ""
                    }`}
                    aria-current={activeId === id ? "true" : undefined}
                  >
                    {label}
                  </a>
                ))}
              </nav>
            </Card>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* Example context inside MAIN */}
          <ExampleContextCard system={homeFeed} />

          {/* =============================================================== */}
          {/* A) Where to cache                                                */}
          {/* =============================================================== */}
          <Section
            id="where"
            title="A) Where to cache"
            subtitle="Think in layers: client → edge → gateway/proxy → service → database. Each layer has different risk and payoff."
          >
            <Template title="Template — choose cache layers safely">
{`1) Start with the edge (CDN) for bytes:
   • static assets + media
   • biggest win: egress + tail latency

2) Add a service cache (Redis/Memcached) for hot objects/lists:
   • reduces DB QPS and read amplification

3) Add in-proc cache only for micro-hot data:
   • small, bounded (Caffeine/Guava)
   • protects p95 but must be conservative

4) Use client cache for static/semi-static:
   • Cache-Control + ETag
   • content-hash filenames for perfect caching

Rule of thumb:
• The closer to user, the bigger the byte savings
• The closer to DB, the bigger the QPS savings
• The more personalized the data, the higher the risk`}
            </Template>

            <Example title="Example — Home Feed cache layers">
{`Client:
• App caches static JS/CSS bundles (hashed) and some images

CDN/Edge:
• All media (images/video segments) cached with long TTL + versioned URLs
• Origin shield enabled so spikes don’t hit origin

Gateway/Proxy:
• Coalesce duplicate requests for same user feed rebuild (if supported)

Service (Redis):
• feed:head:<user> (top N postIds) short TTL
• post:<id> and author/profile fragments TTL minutes
• negative cache for missing post IDs briefly

Database:
• rely on buffer pool, but do not treat DB cache as your “app cache”`}
            </Example>

            <Pitfalls
              items={[
                "Caching personalized responses at CDN without correct Vary → privacy leak",
                "Skipping CDN and trying to “Redis your way” out of media egress",
                "Using DB query cache as a scaling plan (often unpredictable and invalidates broadly)",
                "Letting in-proc cache grow unbounded (OOM or GC pain)",
              ]}
            />

            <Takeaway>
              {`I use CDN for bytes, Redis for hot objects/lists, and small in-proc caches for micro-hot reads—personalized responses are carefully scoped to avoid leaks.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* B) What to cache                                                 */}
          {/* =============================================================== */}
          <Section
            id="what"
            title="B) What to cache"
            subtitle="Cache what is reused, expensive, and safe to serve slightly stale—with a clear invalidation story."
          >
            <Template title="Template — pick cache candidates">
{`Pick items that are:
• High reuse (many reads per write)
• Expensive to compute or fetch
• Small enough to store
• Safe if briefly stale

Typical categories:
1) Objects by ID: user, post, profile
2) Lists / heads: top-N feed, trending IDs
3) Derived results: permissions, feature flags, exchange rates
4) Rendered fragments: precomposed JSON/HTML (high risk: vary rules)
5) Negative cache: 404s briefly to prevent hot-miss storms

Avoid caching:
• Anything that is user-specific unless key includes identity + policy
• Highly volatile correctness-critical values (unless very short TTL / write-through)`}
            </Template>

            <Example title="Example — Home Feed: what to cache">
{`Cache objects:
• post:<postId> (text + metadata) TTL 5–15m
• user:<userId>:profile TTL 15–60m

Cache list heads:
• feed:head:<userId> TTL 30–120s (soft TTL), hard TTL 10m
• comments:top:<postId> TTL 30–120s

Derived:
• permissions:<viewerId>:<ownerId> TTL short + version token

Negative cache:
• post:missing:<postId> TTL 30–60s (prevents repeated DB misses)`}
            </Example>

            <Pitfalls
              items={[
                "Caching query results with unbounded keys (key explosion)",
                "Caching giant payloads (serialization + memory blowup)",
                "Caching authorization decisions without including relevant policy version",
                "No negative caching → hot-miss floods when IDs are probed",
              ]}
            />

            <Takeaway>
              {`I cache objects by ID and the feed head; I keep TTLs aligned to freshness needs and I negative-cache misses briefly to stop hot-miss storms.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* C) Invalidation                                                  */}
          {/* =============================================================== */}
          <Section
            id="invalidation"
            title="C) Invalidation (TTL, event busting, versioned keys)"
            subtitle="Cache invalidation is hard—so use a small set of proven patterns and keep them consistent."
          >
            <Template title="Template — invalidation patterns that scale">
{`1) TTL as the default:
   • simplest, works for semi-static and lists
   • jitter TTL ±10–20% to avoid synchronized expiry

2) Explicit busting (event-based):
   • on write, emit “entity changed” event
   • consumers delete or update cache keys
   • use Outbox/CDC so you don’t miss events

3) Versioned keys / tokens:
   • key contains version token (v7)
   • bump token to instantly invalidate many keys
   • old generations expire naturally via TTL

Recommended combo:
• TTL + event busting for objects
• soft/hard TTL + refresh-ahead for hot lists`}
            </Template>

            <Example title="Example — Home Feed invalidation story">
{`Post updated:
• DB write succeeds
• Outbox publishes PostUpdated(postId)
• Cache consumer deletes post:<postId> and related fragments
• Feed head remains valid (it contains IDs), hydration pulls fresh post on next read

Feed head:
• Use soft TTL 60s and refresh-ahead
• Keep hard TTL 10m as safety net
• If ranking logic changes: bump feed version token and rebuild gradually`}
            </Example>

            <Pitfalls
              items={[
                "Dual-writes without outbox/CDC (cache and DB drift)",
                "No TTL on versioned keys (old generations never leave)",
                "Aggressive global purges during peak (self-inflicted outage)",
                "Treating TTL as correctness guarantee (it’s a freshness knob, not truth)",
              ]}
            />

            <Takeaway>
              {`TTL is my default; correctness-sensitive objects also use event-based busting via outbox/CDC, and hot lists use soft/hard TTL with refresh-ahead.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* D) Consistency patterns                                           */}
          {/* =============================================================== */}
          <Section
            id="consistency"
            title="D) Consistency patterns"
            subtitle="Choose the pattern per data type: correctness vs latency vs complexity."
          >
            <Template title="Template — map data → cache pattern">
{`Cache-aside (most common):
• Read: cache → miss → DB → set
• Write: DB → invalidate/update cache

Read-through:
• cache layer loads DB on miss (good for coalescing)

Write-through:
• write cache + DB synchronously (good for counters if correctness matters)

Write-behind / write-back:
• write cache first, DB async (fast but risky unless cache is durable and replayable)

Refresh-ahead:
• refresh hot keys before they expire to protect p95/p99

Rule:
• Use cache-aside unless you have a strong reason not to.`}
            </Template>

            <Example title="Example — Home Feed: which pattern where">
{`Cache-aside:
• post:<id>, user:<id>:profile

Read-through + coalescing:
• feed:head:<userId> (many concurrent reads)

Write-through:
• like_count and comment_count (if you need fast reads + strong-ish correctness)

Refresh-ahead:
• top feed head for active users (prevents p99 spikes when TTL expires)`}
            </Example>

            <Pitfalls
              items={[
                "Write-behind on correctness-critical data without durability (data loss risk)",
                "Write-through everywhere (hurts write latency and availability)",
                "Refresh-ahead without bounds (background refresh becomes a thundering herd)",
              ]}
            />

            <Takeaway>
              {`Cache-aside for safety, write-through for some counters, and refresh-ahead for hot lists—pattern depends on correctness and tail-latency needs.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* E) Eviction & sizing                                              */}
          {/* =============================================================== */}
          <Section
            id="eviction"
            title="E) Eviction & sizing"
            subtitle="Eviction policy + memory sizing decides whether your cache is stable or constantly thrashing."
          >
            <Template title="Template — eviction & sizing plan">
{`1) Pick eviction/admission:
• LRU: good for recency
• LFU/TinyLFU: protects from one-hit wonders
• segmented policies (ARC/2Q): resist scans

2) Measure working set:
• plot hit ratio vs memory (curve)
• aim for where extra memory gives diminishing returns

3) Headroom:
• keep 20–40% free memory to avoid sudden evictions and latency spikes

4) Hybrid caches:
• small in-proc cache for micro-hot reads
• distributed cache for shared reuse

5) Watch cardinality:
• list caches can explode key count; add bucketing/versioning and TTLs`}
            </Template>

            <Example title="Example — Home Feed cache sizing intuition">
{`Data shape:
• post objects are reused across many users (good cache reuse)
• feed heads are per-user (high cardinality; keep TTL short)

Policy:
• Use TinyLFU/LFU-like admission for post objects
• Keep per-user feed head small (top N IDs only) and short-lived
• Reserve memory for shared objects; cap per-user keys to avoid key explosion`}
            </Example>

            <Pitfalls
              items={[
                "Caching per-user large blobs (memory gets eaten by cardinality)",
                "LRU without admission control (one-hit wonders push out hot keys)",
                "No headroom (eviction storms at peak)",
                "Oversized values (serialization + network overhead dominates)",
              ]}
            />

            <Takeaway>
              {`For mixed workloads I prefer TinyLFU-style admission with LRU eviction, keep headroom, and cap high-cardinality per-user caches.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* F) Stampede protection                                            */}
          {/* =============================================================== */}
          <Section
            id="stampede"
            title="F) Stampede protection"
            subtitle="A cache miss can amplify into an outage. Stampede protection is non-negotiable for hot keys."
          >
            <Template title="Template — stop the thundering herd">
{`1) Request coalescing (single-flight):
   • one request rebuilds the value
   • others wait or serve stale

2) Locks with TTL:
   • Redis SET NX PX=... to acquire lock
   • use fencing tokens for safety

3) Jitter TTL:
   • randomize expiry times

4) Soft/hard TTL:
   • serve stale within hard TTL while refreshing in background

5) Backpressure:
   • cap concurrent rebuilds
   • shed optional work (degrade gracefully)`}
            </Template>

            <Example title="Example — Home Feed stampede prevention">
{`Problem:
• many clients open app at the same time → feed head expires → stampede

Solution:
• soft TTL 60s: serve stale + trigger background refresh
• hard TTL 10m: never hammer DB during refresh failures
• single-flight per user: only one refresh builds the feed head
• cap refresh concurrency globally to protect downstream services`}
            </Example>

            <Code title="Snippet — simple Redis lock for refresh (conceptual)">
{`// Acquire lock (10s). Only the winner rebuilds.
SET lock:feed:head:<userId> <token> NX PX 10000

// Winner rebuilds then writes cache
SET feed:head:<userId> <value> PX <ttl_ms>

// Release lock (verify token to avoid deleting someone else's lock)
if GET lock == token then DEL lock`}
            </Code>

            <Pitfalls
              items={[
                "No coalescing (every request rebuilds on miss)",
                "Locks without TTL (deadlocks)",
                "Locks without fencing tokens (stale owner deletes new lock)",
                "No backpressure (refresh job floods DB under load)",
              ]}
            />

            <Takeaway>
              {`Hot keys use single-flight and soft/hard TTL. We would rather serve slightly stale than stampede the DB.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* G) Metrics & alarms                                               */}
          {/* =============================================================== */}
          <Section
            id="metrics"
            title="G) Metrics & alarms"
            subtitle="If cache health isn’t visible, you’ll learn about it from an outage or a bill."
          >
            <Template title="Template — cache observability checklist">
{`Core:
• hit ratio (overall + per namespace)
• byte hit ratio (bytes served from cache vs origin)
• latency: cache get/set p50/p95/p99

Stability:
• evictions/sec, memory used, fragmentation
• key cardinality per namespace
• error rates (timeouts, connection pool saturation)

Downstream impact:
• DB QPS and p95 latency
• upstream request latency when cache degrades

Alarms:
• hit ratio drop > 10% for 5m
• eviction surge
• cache latency p95 jumps
• connection pool > 80% or timeouts spike`}
            </Template>

            <Example title="Example — Home Feed: alarms that matter">
{`Key alarms:
• feed:head hit ratio drop → likely TTL misconfig or stampede
• post:<id> miss spike → invalidation bug or cache flush
• Redis p95 latency ↑ → network saturation or CPU pressure
• Origin egress ↑ + CDN hit ratio ↓ → cache key/headers regression`}
            </Example>

            <Takeaway>
              {`We alert on hit ratio drops, eviction storms, and cache latency, plus downstream DB QPS—cache metrics must correlate to user SLOs.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* H) Practical examples                                             */}
          {/* =============================================================== */}
          <Section
            id="examples"
            title="H) Practical examples (copy/paste patterns)"
            subtitle="These are the most reusable interview patterns across many systems."
          >
            <Example title="Example — Post object cache (safe cache-aside)">
{`Key: post:<postId>:v1
TTL: 5–15 minutes + jitter
Invalidate: PostUpdated event deletes post:<postId>:*
Notes: keep payload small; store compressed if large; avoid over-fetching`}
            </Example>

            <Example title="Example — Feed head cache (hot list with soft/hard TTL)">
{`Key: feed:head:<userId>:v7
Value: [postId1, postId2, ... postIdN]
Soft TTL: 60–120s (serve stale and refresh)
Hard TTL: 10m (safety)
Stampede: single-flight per user + global refresh cap`}
            </Example>

            <Example title="Example — CDN media caching (huge egress saver)">
{`URL: /media/<contentHash>/w=640.webp
Headers:
  Cache-Control: public, max-age=31536000, immutable
Notes:
• versioned URLs so no purges needed
• origin shielding on
• signed URLs only when private access is required`}
            </Example>
          </Section>

          {/* =============================================================== */}
          {/* I) Keys & HTTP headers                                            */}
          {/* =============================================================== */}
          <Section
            id="keys-headers"
            title="I) Key design & HTTP headers"
            subtitle="Cache keys define correctness. If the key doesn’t capture identity/policy, you risk leaks."
          >
            <Template title="Template — safe key structure">
{`Key format:
• <namespace>:<id>[:subscope][:version]

Include in key when relevant:
• user identity (viewerId) for personalized data
• locale / device class if response differs
• policy version for authorization/visibility
• experiment bucket for A/B variants`}
            </Template>

            <Example title="Example — Home Feed keys (good vs dangerous)">
{`Good:
• feed:head:<userId>:v7
• post:<postId>:v1
• perms:<viewerId>:<ownerId>:policyV3

Dangerous:
• feed:head:v7                (no user identity → leaks)
• profile:<userId>            (no version/policy; invalidation pain)
• search:<q> without filters  (key misses important dimensions)`}
            </Example>

            <Code title="Snippet — HTTP caching header patterns">
{`Static assets (hashed filenames):
Cache-Control: public, max-age=31536000, immutable

Shared dynamic (CDN can cache):
Cache-Control: public, s-maxage=60, stale-while-revalidate=120
Vary: Accept-Encoding

Personalized:
Cache-Control: private, no-store
# or bypass edge and cache in service with user-scoped keys`}
            </Code>

            <Takeaway>
              {`I design cache keys like API contracts: keys include identity and policy versions to prevent leaks, and version tokens make global invalidation safe.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* J) One-liners                                                     */}
          {/* =============================================================== */}
          <Section id="oneliners" title="J) One-liners to use in the interview">
            <ul className="mb-0">
              <li>
                “I cache in layers: CDN for bytes, Redis for shared hot objects/lists, and small in-proc cache for micro-hot paths.”
              </li>
              <li>
                “Invalidation is TTL by default, event-based for correctness, and version tokens for fast mass purges—with jitter to avoid herds.”
              </li>
              <li>
                “Hot lists use soft/hard TTL + refresh-ahead; single-flight prevents stampedes.”
              </li>
              <li>
                “Cache keys include identity and policy version—correctness and privacy come first.”
              </li>
            </ul>

            {/* Optional in-article ad near bottom */}
            {ADS_ENABLED && (
              <div className="my-4 text-center d-print-none">
                <AdSlot
                  enabled={ADS_ENABLED}
                  adSlot="5201219465"
                  style={{ display: "block", minHeight: 90, textAlign: "center" }}
                />
              </div>
            )}
          </Section>
        </main>

        {/* RIGHT: Sponsored (lg+) */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
